import { useCallback, useState, useRef, useEffect } from "react";
import {
  useConnect as useWagmiConnect,
  useAccount,
  useDisconnect,
} from "wagmi";
import metaMaskConnectionManager from "../utils/metamask-connection-manager";
import ethereumProviderGuard from "../utils/ethereum-provider-guard";
import {
  requestWalletConnection,
  reportConnectionSuccess,
  reportConnectionFailure,
  reportWalletDisconnect,
  getConnectionWaitTime,
} from "../utils/connection-coordinator";

interface UseMetaMaskConnectionReturn {
  connect: () => Promise<void>;
  isConnecting: boolean;
  error: string | null;
  clearError: () => void;
  reconnect: () => Promise<void>;
  connectionHealth: "healthy" | "degraded" | "disconnected";
}

/**
 * Hook for managing MetaMask connections with enhanced error handling and recovery
 */
export const useMetaMaskConnection = (): UseMetaMaskConnectionReturn => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionHealth, setConnectionHealth] = useState<
    "healthy" | "degraded" | "disconnected"
  >("disconnected");
  const { connect: wagmiConnect, connectors } = useWagmiConnect();
  const { disconnect } = useDisconnect();
  const { isConnected } = useAccount();

  const lastConnectionAttempt = useRef<number>(0);
  const connectionHealthCheckInterval = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const reconnectionAttempts = useRef<number>(0);
  const MAX_RECONNECTION_ATTEMPTS = 3;

  // Connection health monitoring
  const checkConnectionHealth = useCallback(async () => {
    if (!isConnected) {
      setConnectionHealth("disconnected");
      return;
    }

    try {
      const ethereum = (window as any).ethereum;
      if (!ethereum) {
        setConnectionHealth("disconnected");
        return;
      }

      // Simple health check by requesting accounts
      await ethereum.request({ method: "eth_accounts" });
      setConnectionHealth("healthy");
    } catch (error: any) {
      if (
        error.message?.includes("stream was destroyed") ||
        error.message?.includes("Lost connection") ||
        error.code === -32603
      ) {
        setConnectionHealth("degraded");
      } else {
        setConnectionHealth("disconnected");
      }
    }
  }, [isConnected]);

  // Automatic reconnection for degraded connections
  const attemptReconnection = useCallback(async () => {
    if (reconnectionAttempts.current >= MAX_RECONNECTION_ATTEMPTS) {
      console.warn("useMetaMaskConnection: Max reconnection attempts reached");
      return;
    }

    reconnectionAttempts.current++;
    console.log(
      `useMetaMaskConnection: Attempting reconnection ${reconnectionAttempts.current}/${MAX_RECONNECTION_ATTEMPTS}`
    );

    try {
      // Reset the connection manager
      metaMaskConnectionManager.reset();

      // Brief disconnect and reconnect
      await disconnect();
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Try to reconnect
      await connect();

      if (isConnected) {
        reconnectionAttempts.current = 0; // Reset on successful reconnection
      }
    } catch (error) {
      console.warn("useMetaMaskConnection: Reconnection failed:", error);
    }
  }, [disconnect, isConnected]);

  const connect = useCallback(async () => {
    if (isConnected) {
      console.log("useMetaMaskConnection: Already connected");
      return;
    }

    if (isConnecting) {
      console.log("useMetaMaskConnection: Connection already in progress");
      throw new Error(
        "Connection request is already in progress. Please check MetaMask."
      );
    }

    // Check if we should wait for other wallet connections
    const waitTime = getConnectionWaitTime("metamask");
    if (waitTime > 0) {
      console.log(
        `useMetaMaskConnection: Waiting ${waitTime}ms for other wallet operations to complete`
      );
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return connect();
    }

    // Request permission to connect through coordinator
    if (!requestWalletConnection("metamask")) {
      console.log(
        "useMetaMaskConnection: Connection request denied by coordinator"
      );
      const message =
        "Another wallet connection is in progress. Please wait a moment and try again.";
      setError(message);
      throw new Error(message);
    }

    // Throttle connection attempts
    const now = Date.now();
    if (now - lastConnectionAttempt.current < 2000) {
      console.log("useMetaMaskConnection: Connection request throttled");
      const message = "Please wait a moment before trying to connect again.";
      setError(message);
      reportConnectionFailure("metamask");
      throw new Error(message);
    }
    lastConnectionAttempt.current = now;

    setIsConnecting(true);
    setError(null);

    try {
      // Signal user-initiated connection to ethereum provider guard
      ethereumProviderGuard.setUserInitiatedConnection(true);

      // Emit event to signal MetaMask connection attempt to other wallet systems
      window.dispatchEvent(new CustomEvent("metamask-connect-initiated"));

      // First, try to connect using our MetaMask connection manager
      console.log("useMetaMaskConnection: Attempting MetaMask connection...");
      await metaMaskConnectionManager.connect();

      // If successful, also connect through wagmi for state management
      const metaMaskConnector = connectors.find(
        (connector) =>
          connector.name.toLowerCase().includes("metamask") ||
          connector.name.toLowerCase().includes("injected")
      );

      if (metaMaskConnector) {
        console.log("useMetaMaskConnection: Connecting through wagmi...");
        await wagmiConnect({ connector: metaMaskConnector });
      } else {
        console.warn(
          "useMetaMaskConnection: MetaMask connector not found in wagmi connectors"
        );
      }

      console.log("useMetaMaskConnection: Connection successful");
      setConnectionHealth("healthy");
      reconnectionAttempts.current = 0; // Reset attempts on successful connection
      reportConnectionSuccess("metamask");
    } catch (err: any) {
      console.error("useMetaMaskConnection: Connection failed:", err);

      let errorMessage = "Failed to connect to MetaMask. Please try again.";

      // Enhanced error handling for stream disconnections
      if (
        err.message?.includes("stream was destroyed") ||
        err.message?.includes("Lost connection") ||
        err.message?.includes("write after a stream was destroyed")
      ) {
        errorMessage = "MetaMask connection lost. Attempting to reconnect...";
        setConnectionHealth("degraded");

        // Try automatic reconnection after a delay
        setTimeout(() => {
          if (!isConnected) {
            attemptReconnection();
          }
        }, 3000);
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.code === 4001) {
        errorMessage =
          "Connection request was rejected. Please try again and approve the connection in MetaMask.";
      } else if (err.code === -32002) {
        errorMessage =
          "Connection request is already pending. Please check MetaMask and try again, or restart your browser if the issue persists.";
      } else if (err.code === -32603) {
        errorMessage =
          "Internal error occurred. Please restart MetaMask and try again.";
      }

      setError(errorMessage);
      setConnectionHealth("disconnected");
      reportConnectionFailure("metamask");
      const connectionError = new Error(errorMessage) as Error & {
        code?: number;
      };
      connectionError.code = err.code;
      throw connectionError;
    } finally {
      setIsConnecting(false);
    }
  }, [
    isConnected,
    isConnecting,
    wagmiConnect,
    connectors,
    attemptReconnection,
  ]);

  // Manual reconnection function
  const reconnect = useCallback(async () => {
    if (isConnecting) return;

    console.log("useMetaMaskConnection: Manual reconnection initiated");
    reconnectionAttempts.current = 0; // Reset attempts for manual reconnection
    await connect();
  }, [connect, isConnecting]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Report disconnection when wallet disconnects
  useEffect(() => {
    if (!isConnected && connectionHealth === "disconnected") {
      reportWalletDisconnect("metamask");
    }
  }, [isConnected, connectionHealth]);

  // Set up health monitoring when connected
  useEffect(() => {
    if (isConnected) {
      // Initial health check
      checkConnectionHealth();

      // Set up periodic health checks
      connectionHealthCheckInterval.current = setInterval(
        checkConnectionHealth,
        30000
      ); // Check every 30 seconds

      // Listen for MetaMask error events
      const handleMetaMaskError = (event: CustomEvent) => {
        console.warn(
          "useMetaMaskConnection: MetaMask error detected:",
          event.detail
        );
        setConnectionHealth("degraded");

        // Auto-attempt reconnection for stream errors
        if (
          event.detail?.error?.message?.includes("stream was destroyed") ||
          event.detail?.error?.message?.includes("Lost connection")
        ) {
          setTimeout(() => {
            if (!isConnected) {
              attemptReconnection();
            }
          }, 2000);
        }
      };

      window.addEventListener(
        "metamask-error",
        handleMetaMaskError as EventListener
      );

      return () => {
        if (connectionHealthCheckInterval.current) {
          clearInterval(connectionHealthCheckInterval.current);
          connectionHealthCheckInterval.current = null;
        }
        window.removeEventListener(
          "metamask-error",
          handleMetaMaskError as EventListener
        );
      };
    } else {
      setConnectionHealth("disconnected");
      reconnectionAttempts.current = 0; // Reset attempts when disconnected

      if (connectionHealthCheckInterval.current) {
        clearInterval(connectionHealthCheckInterval.current);
        connectionHealthCheckInterval.current = null;
      }
    }
  }, [isConnected, checkConnectionHealth, attemptReconnection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (connectionHealthCheckInterval.current) {
        clearInterval(connectionHealthCheckInterval.current);
      }
    };
  }, []);

  return {
    connect,
    isConnecting,
    error,
    clearError,
    reconnect,
    connectionHealth,
  };
};
